import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';

interface CreateOrgData {
  name: string;
  description: string;
}

interface CreateOrganizationModalProps {
  onClose: () => void;
  /** Optional external handler; if omitted the modal calls the API itself */
  onCreate?: (orgData: CreateOrgData) => Promise<boolean>;
  /** Called after a successful create (e.g. to refresh data) */
  onSuccess?: () => void;
}

/**
 * Self-contained modal for creating new organizations.
 * Calls /api/teams/admin/create-organization/ directly when no onCreate prop is supplied.
 */
const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({ onClose, onCreate, onSuccess }) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState<CreateOrgData>({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const defaultCreate = async (orgData: CreateOrgData): Promise<boolean> => {
    const token = localStorage.getItem('access');
    if (!token) throw new Error('Authentication required. Please log in.');
    const response = await fetch(getApiUrl('/api/teams/admin/create-organization/'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(orgData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.detail || `HTTP ${response.status}`);
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.name) {
      setError('Please enter an organization name');
      return;
    }

    try {
      setLoading(true);
      const handler = onCreate ?? defaultCreate;
      const ok = await handler(formData);
      if (ok) {
        setSuccess('Organization created successfully.');
        onSuccess?.();
        setTimeout(() => onClose(), 1200);
      } else {
        setError('Failed to create organization. Please try again.');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      setError(error instanceof Error ? error.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cgm-modal-backdrop">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="cgm-card cgm-modal-panel p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <h3 className="text-2xl cgm-text-primary mb-2">
              Create New Organization
            </h3>
            <p className="cgm-text-secondary text-sm">
              Add a new organization to the system
            </p>
          </div>
          {error && (
            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
          )}
          {success && (
            <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">{success}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium cgm-text-secondary mb-1">
                Organization Name
              </label>
              <input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter organization name"
              />
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium cgm-text-secondary mb-1">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter organization description"
                rows={3}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                  Creating Organization...
                </>
              ) : (
                'Create Organization'
              )}
            </button>
          </form>

          <div className="mt-6 space-y-4 text-center">
            <button
              onClick={onClose}
              className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganizationModal;
